import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  fetchOnboardingOrderDetails,
  fetchOnboardingOrders,
  getProperties,
  fetchKeyContacts,
} from 'src/services/client';
import { requestModulePermissions } from 'src/SharedCustomHooks/UseModulePermissionsAPI/helpers';
import {
  IProperty,
  PropertiesApiResponse,
} from 'src/pages/Reporting/ReportingFormTypes';
import { setSidebarPermissions } from 'src/store/slices/sidebarPermissionsSlice';
import {
  selectAllowedTabList,
  selectSidebarProperties,
  selectSidebarOrders,
  selectSidebarOrderDetails,
  selectSidebarKeyContacts,
} from 'src/store/selectors/sidebarPermissionsSelectors';

const useSidebarPermissions = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);

  const selectedUserSuite: any = useSelector(
    (state: any) => state.userSuites.selectedUserSuite
  );

  const allowedTabList: any = useSelector(selectAllowedTabList);
  const properties: any = useSelector(selectSidebarProperties);
  const orders: any = useSelector(selectSidebarOrders);
  const orderDetails: any = useSelector(selectSidebarOrderDetails);
  const keyContacts: any = useSelector(selectSidebarKeyContacts);

  const propertyId: string | undefined = selectedUserSuite?.propertyId;

  /* TODO(sidebar-permissions-refactor): this hook should own its fetch state
   * (useState + LoadState keyed on propertyId) instead of round-tripping through
   * the persisted `sidebarPermissions` Redux slice. The persisted `hasFetched`
   * flag is what froze the tab list (incl. Announcements) on property switch —
   * we now ignore it and re-key on propertyId. Retire the slice in the follow-up.
   * https://app.asana.com/1/41213529744312/project/1208712340140532/task/1215446120928526?focus=true */
  const lastFetchedPropertyId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    if (lastFetchedPropertyId.current === propertyId) {
      return;
    }

    const fetchData = async () => {
      const [freshTabList, freshProperties, freshKeyContacts] =
        await Promise.all([
          fetchAllowedTabListFromAPI(),
          fetchAndParsePropertiesDataFromAPI(),
          fetchKeyContactsFromAPI(),
        ]);

      const freshOrders: any[] = await fetchOnboardingOrdersFromAPI();
      const freshOrderDetails: any =
        freshOrders.length > 0
          ? await fetchOrderDetailsFromAPI(freshOrders[0].id)
          : null;

      dispatch(
        setSidebarPermissions({
          allowedTabList: freshTabList,
          properties: freshProperties,
          orders: freshOrders,
          orderDetails: freshOrderDetails,
          keyContacts: freshKeyContacts,
        })
      );

      lastFetchedPropertyId.current = propertyId;
    };

    fetchData();
  }, [propertyId]);

  /* TODO: [TECH DEBT] allowedTabList returns an empty array for Admin and Super Admin roles.
   * This is intentional — the Sidebar interprets an empty list as "show all tabs" rather
   * than "show no tabs". This implicit convention is extremely fragile: any future logic
   * that treats an empty allowedTabList as a loading state or error condition will
   * inadvertently break navigation for admin users. A role-aware permission model with
   * an explicit "show all" flag should replace this before any further changes are made
   * to Sidebar tab visibility logic. */
  const fetchAllowedTabListFromAPI = async (): Promise<any[]> => {
    try {
      return await requestModulePermissions(selectedUserSuite.propertyId);
    } catch (error: any) {
      console.error('fetchAllowedTabList error:', error);
      return [];
    }
  };

  const fetchAndParsePropertiesDataFromAPI = async (): Promise<IProperty[]> => {
    try {
      const propertiesResponse: any = await getProperties(
        user.id,
        user?.roles[0]?.role?.name
      );

      if (!propertiesResponse || !propertiesResponse.ok) {
        throw new Error('Failed to fetch properties');
      }

      const propertiesFromApi: PropertiesApiResponse =
        await propertiesResponse.json();

      return propertiesFromApi?.data || [];
    } catch (error) {
      console.error('Error in fetchPropertiesData:', error);
      throw error;
    }
  };

  // Todo: The API contracts here aren't really defined properly yet from the API side for for now in the UI code these are just typed as 'any'.
  const fetchOnboardingOrdersFromAPI = async (): Promise<any[]> => {
    try {
      const response: any = await fetchOnboardingOrders(
        selectedUserSuite.propertyId
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch orders: ${response?.status}`);
      }

      const result = await response.json();
      const rawOrders: any[] = result?.data || [];

      if (rawOrders.length === 0) {
        return [];
      }

      const suiteStatus = selectedUserSuite.status;
      return rawOrders.filter(
        (order) => order.isPostOccupancy === (suiteStatus === 'Occupied')
      );
    } catch (error) {
      console.error('fetchOnboardingOrdersFromAPI error:', error);
      return [];
    }
  };

  const fetchOrderDetailsFromAPI = async (orderId: string): Promise<any> => {
    try {
      const response: any = await fetchOnboardingOrderDetails(
        selectedUserSuite.propertyId,
        orderId,
        selectedUserSuite.id,
        user.id
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch order details: ${response?.status}`);
      }

      const result = await response.json();

      return result?.data || null;
    } catch (error) {
      console.error('fetchOrderDetailsFromAPI error:', error);
      return null;
    }
  };

  const fetchKeyContactsFromAPI = async (): Promise<any[]> => {
    try {
      const response: any = await fetchKeyContacts(
        selectedUserSuite.propertyId
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch key contacts: ${response?.status}`);
      }

      const resultData = await response.json();

      return resultData || [];
    } catch (error) {
      console.error('fetchKeyContactsFromAPI error:', error);
      return [];
    }
  };

  return {
    user,
    selectedUserSuite,
    allowedTabList,
    properties,
    orders,
    orderDetails,
    keyContacts,
  };
};

export default useSidebarPermissions;